/**
 * Auth modal — login, signup, password reset request.
 * Talks to UserAccountController over XHR (shared/auth-request.js, as the
 * sign-in form of the start dialog does) and updates window.RCN_USER + topbar
 * UI without a full page reload. Other modules can call window.openAuthModal()
 * when they need to ensure the user is logged in.
 */

import { postAuthForm } from '../../shared/auth-request';
import { turnstileSize } from '../../shared/turnstile-size';

var initialized = false;
var modalEl = null;
var bodyEl = null;
var titleEl = null;
var errorEl = null;
var tabEls = null;
var appEl = null;
var userBtn = null;
var userMenu = null;
var userMenuName = null;
var userMenuEmail = null;
var mobileAuthNavLink = null;
var currentView = null;
var pendingAction = null;
var lastFocusedEl = null;
var signupTurnstileWidgetId = null;
var signupTurnstileToken = '';
var signupTurnstileMountTimer = null;

function esc(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function t(key, fallback) {
    return fallback;
}

function signupTurnstileConfig() {
    var config = window.RCN_COMMUNITY_SIGNALS_TURNSTILE;
    return config && typeof config === 'object' ? config : {};
}

function isSignupTurnstileEnabled() {
    var config = signupTurnstileConfig();
    return config.enabled === true
        && typeof config.siteKey === 'string'
        && config.siteKey.trim() !== '';
}

function clearSignupTurnstileMountTimer() {
    if (signupTurnstileMountTimer !== null) {
        clearTimeout(signupTurnstileMountTimer);
        signupTurnstileMountTimer = null;
    }
}

function disposeSignupTurnstile() {
    clearSignupTurnstileMountTimer();
    signupTurnstileToken = '';
    if (signupTurnstileWidgetId != null && window.turnstile && typeof window.turnstile.remove === 'function') {
        try { window.turnstile.remove(signupTurnstileWidgetId); } catch (error) {}
    }
    signupTurnstileWidgetId = null;
}

function queueSignupTurnstileMount() {
    clearSignupTurnstileMountTimer();
    signupTurnstileMountTimer = setTimeout(mountSignupTurnstile, 250);
}

function mountSignupTurnstile() {
    if (currentView !== 'signup' || !isSignupTurnstileEnabled() || signupTurnstileWidgetId != null) return;
    var container = bodyEl && bodyEl.querySelector('[data-auth-turnstile]');
    if (!container) return;
    if (!window.turnstile || typeof window.turnstile.render !== 'function') {
        queueSignupTurnstileMount();
        return;
    }

    clearSignupTurnstileMountTimer();
    signupTurnstileToken = '';
    try {
        signupTurnstileWidgetId = window.turnstile.render(container, {
            sitekey: signupTurnstileConfig().siteKey,
            size: turnstileSize(container),
            callback: function(token) {
                signupTurnstileToken = String(token || '');
            },
            'expired-callback': function() {
                signupTurnstileToken = '';
            },
            'error-callback': function() {
                signupTurnstileToken = '';
                showError('Nie udało się załadować zabezpieczenia formularza. Odśwież stronę i spróbuj ponownie.');
            }
        });
    } catch (error) {
        signupTurnstileWidgetId = null;
        signupTurnstileToken = '';
        showError('Nie udało się uruchomić zabezpieczenia formularza.');
    }
}

function viewConfig(view) {
    if (view === 'signup') {
        var formName = 'FrontendSignupForm';
        return {
            title: t('auth.signup.title', 'Załóż konto'),
            tab: 'signup',
            url: window.RCN_AUTH_URLS && window.RCN_AUTH_URLS.signup,
            formName: formName,
            html: function() {
                return ''
                    + '<form class="nm-auth-form" data-auth-form="signup" novalidate>'
                    + '  <div class="nm-auth-form__field">'
                    + '    <label for="nm-auth-signup-display-name">Nazwa wyświetlana</label>'
                    + '    <input class="ms-input" id="nm-auth-signup-display-name" name="' + formName + '[displayName]" type="text" autocomplete="nickname">'
                    + '  </div>'
                    + '  <div class="nm-auth-form__field">'
                    + '    <label for="nm-auth-signup-email">E-mail</label>'
                    + '    <input class="ms-input" id="nm-auth-signup-email" name="' + formName + '[email]" type="email" autocomplete="email" required>'
                    + '  </div>'
                    + '  <div class="nm-auth-form__field">'
                    + '    <label for="nm-auth-signup-password">Hasło</label>'
                    + '    <input class="ms-input" id="nm-auth-signup-password" name="' + formName + '[password]" type="password" autocomplete="new-password" minlength="8" required>'
                    + '  </div>'
                    + '  <div class="nm-auth-form__field">'
                    + '    <label for="nm-auth-signup-password-repeat">Powtórz hasło</label>'
                    + '    <input class="ms-input" id="nm-auth-signup-password-repeat" name="' + formName + '[passwordRepeat]" type="password" autocomplete="new-password" minlength="8" required>'
                    + '  </div>'
                    + '  <label class="nm-auth-form__check">'
                    + '    <input type="checkbox" name="' + formName + '[termsAccepted]" value="1" required>'
                    + '    <span>Akceptuję <a href="/sygnaly-spolecznosci/regulamin" target="_blank" rel="noopener">regulamin i politykę prywatności</a></span>'
                    + '  </label>'
                    + (isSignupTurnstileEnabled() ? '  <div class="nm-auth-form__turnstile" data-auth-turnstile></div>' : '')
                    + '  <button type="submit" class="ms-button ms-button--primary">Załóż konto</button>'
                    + '  <p class="nm-auth-form__footer">Masz już konto? <button type="button" data-auth-view="login">Zaloguj się</button></p>'
                    + '</form>';
            }
        };
    }
    if (view === 'reset') {
        return {
            title: t('auth.reset.title', 'Przypomnij hasło'),
            tab: null,
            url: window.RCN_AUTH_URLS && window.RCN_AUTH_URLS.requestReset,
            formName: 'FrontendPasswordResetRequestForm',
            html: function() {
                var formName = 'FrontendPasswordResetRequestForm';
                return ''
                    + '<form class="nm-auth-form" data-auth-form="reset" novalidate>'
                    + '  <p class="nm-auth-form__footer" style="text-align:left;margin:0 0 6px;">Wyślemy link do resetu hasła na podany adres e-mail.</p>'
                    + '  <div class="nm-auth-form__field">'
                    + '    <label for="nm-auth-reset-email">E-mail</label>'
                    + '    <input class="ms-input" id="nm-auth-reset-email" name="' + formName + '[email]" type="email" autocomplete="email" required>'
                    + '  </div>'
                    + '  <button type="submit" class="ms-button ms-button--primary">Wyślij link</button>'
                    + '  <p class="nm-auth-form__footer"><button type="button" data-auth-view="login">Wróć do logowania</button></p>'
                    + '</form>';
            }
        };
    }
    return {
        title: t('auth.login.title', 'Zaloguj się'),
        tab: 'login',
        url: window.RCN_AUTH_URLS && window.RCN_AUTH_URLS.login,
        formName: 'FrontendLoginForm',
        html: function() {
            var formName = 'FrontendLoginForm';
            return ''
                + '<form class="nm-auth-form" data-auth-form="login" novalidate>'
                + '  <div class="nm-auth-form__field">'
                + '    <label for="nm-auth-login-email">E-mail</label>'
                + '    <input class="ms-input" id="nm-auth-login-email" name="' + formName + '[email]" type="email" autocomplete="email" required>'
                + '  </div>'
                + '  <div class="nm-auth-form__field">'
                + '    <label for="nm-auth-login-password">Hasło</label>'
                + '    <input class="ms-input" id="nm-auth-login-password" name="' + formName + '[password]" type="password" autocomplete="current-password" required>'
                + '  </div>'
                + '  <div class="nm-auth-form__row">'
                + '    <label><input type="checkbox" name="' + formName + '[rememberMe]" value="1"> Zapamiętaj mnie</label>'
                + '    <button type="button" class="nm-auth-form__link" data-auth-view="reset">Zapomniałem hasła</button>'
                + '  </div>'
                + '  <button type="submit" class="ms-button ms-button--primary">Zaloguj się</button>'
                + '  <p class="nm-auth-form__footer">Nie masz konta? <button type="button" data-auth-view="signup">Załóż konto</button></p>'
                + '</form>';
        }
    };
}

function showError(message) {
    if (!errorEl) return;
    if (!message) {
        errorEl.hidden = true;
        errorEl.textContent = '';
        return;
    }
    errorEl.textContent = message;
    errorEl.hidden = false;
}

function setView(view) {
    disposeSignupTurnstile();
    var cfg = viewConfig(view);
    currentView = view;
    if (titleEl) titleEl.textContent = cfg.title;
    if (bodyEl) bodyEl.innerHTML = cfg.html();
    showError('');
    if (tabEls) {
        tabEls.forEach(function(el) {
            el.classList.toggle('is-active', el.getAttribute('data-auth-view') === cfg.tab);
        });
    }
    setTimeout(function() {
        var firstInput = bodyEl && bodyEl.querySelector('input');
        if (firstInput) firstInput.focus();
        mountSignupTurnstile();
    }, 30);
}

function openModal(view, opts) {
    if (!modalEl) return;
    pendingAction = (opts && typeof opts.pendingAction === 'function') ? opts.pendingAction : null;
    lastFocusedEl = document.activeElement;
    modalEl.hidden = false;
    setView(view || 'login');
}

function closeModal() {
    if (!modalEl) return;
    disposeSignupTurnstile();
    modalEl.hidden = true;
    showError('');
    pendingAction = null;
    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
        try { lastFocusedEl.focus(); } catch (e) { /* noop */ }
    }
}

function authUrl(key, fallback) {
    return (window.RCN_AUTH_URLS && window.RCN_AUTH_URLS[key]) || fallback || '#';
}

function updateMobileAuthNavLink(user) {
    if (!mobileAuthNavLink) {
        mobileAuthNavLink = document.querySelector('[data-auth-nav-link]');
    }
    if (!mobileAuthNavLink) return;

    if (user) {
        mobileAuthNavLink.setAttribute('href', authUrl('dashboard', authUrl('profile', '/konto/panel')));
        mobileAuthNavLink.textContent = 'Mój panel';
    } else {
        mobileAuthNavLink.setAttribute('href', authUrl('login', '/konto/logowanie'));
        mobileAuthNavLink.textContent = 'Zaloguj się';
    }
}

function applyUserUI(user) {
    window.RCN_USER = user || null;
    if (appEl) appEl.classList.toggle('is-logged', !!user);
    if (userMenuName) userMenuName.textContent = user ? (user.displayName || user.email || '') : '';
    if (userMenuEmail) userMenuEmail.textContent = user ? (user.email || '') : '';
    var adminPanelLink = document.querySelector('[data-admin-panel-link]');
    if (adminPanelLink) adminPanelLink.hidden = !(user && user.isAdmin === true);
    if (userBtn) {
        userBtn.setAttribute('aria-expanded', 'false');
    }
    if (userMenu) userMenu.hidden = true;
    updateMobileAuthNavLink(user || null);
    if (typeof CustomEvent === 'function') {
        document.dispatchEvent(new CustomEvent('rcn:auth:user-applied', { detail: { user: user || null } }));
    }
}

function firstErrorMessage(errors) {
    if (!errors || typeof errors !== 'object') return '';
    var keys = Object.keys(errors);
    for (var i = 0; i < keys.length; i++) {
        var list = errors[keys[i]];
        if (Array.isArray(list) && list.length) return list[0];
        if (typeof list === 'string' && list) return list;
    }
    return '';
}

function submitForm(form) {
    var cfg = viewConfig(currentView);
    if (!cfg.url) {
        showError('Brak adresu endpointu. Odśwież stronę.');
        return;
    }
    if (currentView === 'signup' && isSignupTurnstileEnabled() && !signupTurnstileToken) {
        showError('Potwierdź weryfikację antyspamową i spróbuj ponownie.');
        return;
    }
    var submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    showError('');

    var formData = new FormData(form);
    if (currentView === 'signup' && signupTurnstileToken) {
        formData.append('cf-turnstile-response', signupTurnstileToken);
    }

    // The page's CSRF token and the JSON headers come with the shared request.
    postAuthForm(cfg.url, formData)
        .then(function(result) {
            if (submitBtn) submitBtn.disabled = false;
            var payload = result.payload || {};

            if (result.ok && payload.success) {
                // Password reset and signup do not sign anyone in — both end with
                // an e-mail to open — so the modal stays on a confirmation rather
                // than running a pending action that would still hit the gate.
                if (currentView === 'reset' || !payload.user) {
                    disposeSignupTurnstile();
                    showError('');
                    bodyEl.innerHTML = '<div class="nm-auth-form__footer" style="text-align:left;">' +
                        esc(payload.message || (currentView === 'reset'
                            ? 'Sprawdź skrzynkę e-mail — wysłaliśmy link do resetu hasła.'
                            : 'Sprawdź skrzynkę e-mail, aby potwierdzić konto.')) +
                        '</div>';
                    return;
                }
                var actionAfterLogin = pendingAction;
                applyUserUI(payload.user || null);
                closeModal();
                if (typeof actionAfterLogin === 'function') {
                    setTimeout(function() {
                        try { actionAfterLogin(); } catch (e) { /* noop */ }
                    }, 0);
                }
                return;
            }

            var msg = (payload && payload.message)
                || firstErrorMessage(payload && payload.errors)
                || 'Nie udało się zakończyć operacji. Spróbuj ponownie.';
            showError(msg);
            if (currentView === 'signup' && signupTurnstileWidgetId != null && window.turnstile && typeof window.turnstile.reset === 'function') {
                signupTurnstileToken = '';
                try { window.turnstile.reset(signupTurnstileWidgetId); } catch (error) {}
            }
        })
        .catch(function() {
            if (submitBtn) submitBtn.disabled = false;
            showError('Błąd sieci. Sprawdź połączenie i spróbuj ponownie.');
        });
}

function adjustUserMenuPosition() {
    if (!userMenu || userMenu.hidden) return;
    userMenu.style.left = '';
    userMenu.style.right = '';
    var rect = userMenu.getBoundingClientRect();
    var padding = 12;
    var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    if (rect.right > viewportWidth - padding) {
        var overshoot = rect.right - (viewportWidth - padding);
        userMenu.style.right = (parseFloat(getComputedStyle(userMenu).right) || 0) + overshoot + 'px';
    }
    if (rect.left < padding) {
        userMenu.style.left = padding + 'px';
        userMenu.style.right = 'auto';
    }
}

function bindUserButton() {
    if (!userBtn) return;
    userBtn.addEventListener('click', function() {
        if (!window.RCN_USER) {
            openModal('login');
            return;
        }
        var open = userMenu && userMenu.hidden === false;
        if (userMenu) {
            userMenu.hidden = open;
            if (!open) adjustUserMenuPosition();
        }
        userBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
    });

    window.addEventListener('resize', function() {
        if (userMenu && !userMenu.hidden) adjustUserMenuPosition();
    });

    document.addEventListener('click', function(e) {
        if (!userMenu || userMenu.hidden) return;
        if (e.target === userBtn || userBtn.contains(e.target)) return;
        if (userMenu.contains(e.target)) return;
        userMenu.hidden = true;
        userBtn.setAttribute('aria-expanded', 'false');
    });

}

// Bound unconditionally: pages that embed the modal without the map topbar
// (e.g. the listing wizard) have no user button but still need Escape to close.
function bindEscape() {
    document.addEventListener('keydown', function(e) {
        if (e.key !== 'Escape') return;
        if (modalEl && !modalEl.hidden) {
            closeModal();
            return;
        }
        if (userMenu && !userMenu.hidden) {
            userMenu.hidden = true;
            if (userBtn) userBtn.setAttribute('aria-expanded', 'false');
        }
    });
}

export function initAuthModal() {
    if (initialized) return;
    initialized = true;

    modalEl = document.getElementById('nm-auth-modal');
    bodyEl = document.getElementById('nm-auth-body');
    titleEl = document.getElementById('nm-auth-title');
    errorEl = document.getElementById('nm-auth-error');
    appEl = document.querySelector('.nm-app');
    userBtn = document.getElementById('nm-user-btn');
    userMenu = document.getElementById('nm-user-menu');
    userMenuName = document.getElementById('nm-user-menu-name');
    userMenuEmail = document.getElementById('nm-user-menu-email');
    mobileAuthNavLink = document.querySelector('[data-auth-nav-link]');
    tabEls = modalEl ? Array.prototype.slice.call(modalEl.querySelectorAll('.nm-auth-modal__tab')) : [];

    applyUserUI(window.RCN_USER || null);
    bindUserButton();
    bindEscape();

    if (modalEl) {
        modalEl.addEventListener('click', function(e) {
            if (e.target.closest('[data-auth-close]')) {
                closeModal();
                return;
            }
            var viewBtn = e.target.closest('[data-auth-view]');
            if (viewBtn && modalEl.contains(viewBtn)) {
                setView(viewBtn.getAttribute('data-auth-view'));
            }
        });

        modalEl.addEventListener('submit', function(e) {
            var form = e.target.closest('form[data-auth-form]');
            if (!form) return;
            e.preventDefault();
            submitForm(form);
        });
    }

    window.openAuthModal = openModal;
    window.RCN_AUTH = window.RCN_AUTH || {};
    window.RCN_AUTH.applyUser = applyUserUI;
    window.RCN_AUTH.close = closeModal;
}
